import React, { type JSX, forwardRef } from "react";
import {
    AriaButtonOptions,
    useButton,
    useFocusRing,
    useObjectRef,
} from "react-aria";

import { SimpleLinkBlock } from "@reactivated";

import { Clickable as BaseClickable } from "@thelabnyc/thelabui/src/components/Clickable";
import { ClickableProps as BaseClickableProps } from "@thelabnyc/thelabui/src/components/Clickable/types";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { SVGName, Svg, allSvgNames } from "../Svg";

import styles from "./index.module.scss";

export type ClickableProps = BaseClickableProps &
    AriaButtonOptions<"button" | "a"> & {
        simpleLink?: SimpleLinkBlock;
        /**
         * If a className is provided, it will be placed in a div that will
         * otherwise not exist
         */
        innerClassName?: string;
    };

const simpleLinkBlock = (props: SimpleLinkBlock | undefined) => {
    if (!props) return {};
    const target = props.link_type === "external" ? "_blank" : "_self";
    const href = props.url ?? undefined;

    return {
        target,
        href,
    };
};

const Icon = ({
    icon,
    target,
}: {
    icon: string | JSX.Element | undefined;
    target: string | undefined;
}) => {
    /** Design says every external link gets the icon */
    if (target === "_blank")
        return (
            <Svg name="external" visuallyHiddenText={null} aria-hidden={true} />
        );
    /** No icon, no cry */
    if (!icon) return <></>;
    /** If it exists and it's not a string, then it's JSX, just return it */
    if (typeof icon !== "string") return icon;
    /** Otherwise go get it from the Svg component */
    if ((allSvgNames as ReadonlyArray<string>).includes(icon))
        return (
            <Svg
                name={icon as SVGName}
                visuallyHiddenText={null}
                aria-hidden={true}
            />
        );
};

export const Clickable = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>((props, ref) => {
    ref = useObjectRef(ref);
    const { buttonProps } = useButton(props, ref);
    const { focusProps, isFocused, isFocusVisible } = useFocusRing(props);
    const {
        onPress,
        isDisabled,
        simpleLink,
        innerClassName,
        ...propsWithoutOddballs
    } = props;

    const simpleLinkProps = simpleLinkBlock(simpleLink);

    const clickableProps = {
        ...buttonProps,
        ...focusProps,
        ...propsWithoutOddballs,
        ...simpleLinkProps,
    };

    const children = simpleLink?.display_name
        ? simpleLink.display_name
        : props.children;

    return (
        <BaseClickable
            ref={ref}
            {...clickableProps}
            className={concatClassNames([styles.base, props.className])}
            data-focused={isFocused || undefined}
            data-focus-visible={isFocusVisible || undefined}
        >
            {innerClassName ? (
                <div className={innerClassName}>
                    {children}
                    <Icon
                        icon={clickableProps.icon}
                        target={clickableProps.target}
                    />
                </div>
            ) : (
                <>
                    {children}
                    <Icon
                        icon={clickableProps.icon}
                        target={clickableProps.target}
                    />
                </>
            )}
        </BaseClickable>
    );
});

export const ButtonPrimary = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>(function ButtonPrimary(props, ref) {
    return (
        <Clickable
            {...props}
            ref={ref}
            className={concatClassNames([
                styles.primaryButton,
                props.disabled ? styles.disabled : undefined,
                props.className ?? undefined,
            ])}
            innerClassName={styles.clickableContent}
        >
            {props.children}
        </Clickable>
    );
});

export const TextLink = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>(function TextLink(props, ref) {
    return (
        <Clickable
            {...props}
            ref={ref}
            className={concatClassNames([
                styles.buttonSecondary,
                props.disabled ? styles.disabled : undefined,
                props.className ?? undefined,
            ])}
            innerClassName={styles.clickableContent}
        >
            {props.children}
        </Clickable>
    );
});

export const ButtonPlayPause = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>(function ButtonPlayPause({ simpleLink, icon, ...props }, ref) {
    return (
        <Clickable
            {...props}
            ref={ref}
            className={concatClassNames([
                styles.playPauseButton,
                props.disabled ? styles.disabled : undefined,
                props.className ?? undefined,
            ])}
        >
            {props.children}
        </Clickable>
    );
});
